/**
 * Iter 45 — Student-level fidelity card.
 *
 * Per-student minutes/fidelity view — one card on the Student Workspace
 * Overview tab. Renders each active service commitment with:
 *   • status chip (met / over / behind / on_track)
 *   • progress bar (delivered / committed · minutes this week or month)
 *   • scheduled minutes (what's coming + already delivered) for context
 *   • one-line copy that tells the provider what to do
 *
 * Cadence-aware — copy says "min this week" for weekly commitments and
 * "min this month" for monthly.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GaugeIcon } from "./GaugeIcon";
import { FidelityChip, FidelityProgressBar, fidelityBannerCopy, FIDELITY_META } from "./FidelityVisuals";
import api from "@/lib/api";

export function StudentFidelityCard({ studentId, providerId, testid = "student-fidelity-card" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!studentId || !providerId) return;
    setLoading(true);
    api
      .getProviderFidelity({ provider_id: providerId, student_id: studentId })
      .then((r) => {
        const me = (r.data?.students || []).find((s) => s.student_id === studentId);
        setData(me || null);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [studentId, providerId]);

  if (!studentId || !providerId) return null;

  return (
    <Card className="bg-white rounded-2xl border border-[#E5E3DB] shadow-sm" data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-[#1F2937] heading-font flex items-center gap-2">
          <GaugeIcon size={18} /> Service fidelity
          {data?.status_rollup && data.status_rollup !== "no_data" && (
            <FidelityChip status={data.status_rollup} size="sm" testid="student-fidelity-rollup-chip" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div className="h-16 rounded-lg bg-[#F3F1EC] animate-pulse" />
        )}
        {!loading && (!data || !data.commitments?.length) && (
          <p className="text-[12px] text-[#6B7280] italic">
            No active service commitment on file. Add an obligation in Service Demographics to track fidelity.
          </p>
        )}
        {!loading && data?.commitments?.map((c, i) => {
          const copy = fidelityBannerCopy(c.status, c.delivered_minutes, c.target_minutes, c.cadence);
          const meta = FIDELITY_META[c.status] || FIDELITY_META.on_track;
          return (
            <div
              key={`${c.service_type || "c"}-${i}`}
              data-testid={`student-fidelity-commitment-${i}`}
              className="p-3 rounded-lg border border-[#ECEAE4] bg-white space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-semibold text-[#1F2937] uppercase tracking-wide truncate">
                    {c.label}
                  </span>
                  <FidelityChip status={c.status} size="xs" />
                </div>
                <span className="text-[10px] text-[#9B9890]">
                  {c.period_start} → {c.period_end}
                </span>
              </div>
              <FidelityProgressBar
                delivered={c.delivered_minutes}
                committed={c.target_minutes}
                cadence={c.cadence}
                testid={`student-fidelity-bar-${i}`}
              />
              <p className={`text-[11px] ${meta.tone === "red" ? "text-[#B85C5C]" : meta.tone === "amber" ? "text-[#9C6B2E]" : "text-[#6B7280]"}`}>
                {copy}
                {c.scheduled_minutes > 0 && c.scheduled_minutes !== c.delivered_minutes && (
                  <span className="text-[#9B9890] italic">
                    {" "}· {c.scheduled_minutes} min planned incl. upcoming
                  </span>
                )}
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
